import React, { useEffect, useRef, useState } from 'react'
import { BRIEF_ALL_VIEW, ViewType } from '@disco/constants/ViewTypes'
import { useDispatch } from 'hooks/use-redux'
import { Container, SearchTextField } from 'components/ds'
import { switchPaneView } from '@disco/state-management/actions/ExplorerActions'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { ExplorerPane } from '@disco/types/types/explorer'
import { useNavigate } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'
import { usePrevious } from 'hooks/use-previous'

type Props = {
  pane: ExplorerPane
}

export const BriefListFilter = ({ pane }: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const [searchTerm, setSearchTerm] = useState(pane?.searchTerm || '')
  const navigate = useNavigate()
  const previousView = usePrevious(pane?.view, true)
  const isTypingRef = useRef(false)

  useEffect(() => {
    if (previousView !== pane?.view) {
      navigate('/briefs')
    }
  }, [pane?.view, navigate, previousView])

  useEffect(() => {
    // sync local searchTerm state with pane.searchTerm from redux
    if (!isTypingRef.current && pane?.searchTerm !== searchTerm) {
      setSearchTerm(pane?.searchTerm || '')
    }

    // only run this effect when pane.searchTerm changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pane?.searchTerm])

  const debouncedQuery = useDebouncedCallback(input => {
    if (pane) {
      dispatch(
        switchPaneView(pane, (pane?.view as ViewType) || BRIEF_ALL_VIEW, {
          searchTerm: input,
        }),
      )
      navigate('/briefs')
      dispatch(
        sendMixpanelEvent('Search', {
          Context: 'Briefs',
        }),
      )
    }
    // reset typing flag after debounce
    isTypingRef.current = false
  }, 300)

  const handleInputChange = (input: string) => {
    isTypingRef.current = true
    setSearchTerm(input)
    debouncedQuery(input)
  }

  if (!pane) return

  return (
    <Container align="center">
      <SearchTextField
        value={searchTerm}
        type="text"
        size="2"
        onChange={e => handleInputChange(e.target.value)}
        placeholder={'Search Briefs'}
      />
    </Container>
  )
}
